import React, { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { storage } from '@/utils/storage';
import { tokenHasPermission } from '@/features/auth/utils/jwtPermissions';
import {
  createArticle,
  deleteArticle,
  getArticleById,
  toArticlesApiError,
  updateArticle,
} from '../api/articlesApi';
import ArticlePanel, { type ArticlePanelMode } from '../components/ArticlePanel';
import ArticleTable from '../components/ArticleTable';
import ArticleToolbar from '../components/ArticleToolbar';
import { useArticles } from '../hooks/useArticles';
import type { Article, CreateArticleInput, UpdateArticleInput } from '../types';

const AdminArticlesPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    query,
    pageData,
    isInitialLoading,
    isTableLoading,
    loadError,
    setPage,
    setPageSize,
    setSort,
    refresh,
    retry,
  } = useArticles();
  const [panelMode, setPanelMode] = useState<ArticlePanelMode>('view');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingArticleId, setPendingArticleId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Article | null>(null);
  const token = storage.getAccessToken();
  const canCreate = tokenHasPermission(token, 'ARTICLE_CREATE');
  const canUpdate = tokenHasPermission(token, 'ARTICLE_UPDATE');
  const canDelete = tokenHasPermission(token, 'ARTICLE_DELETE');

  const openCreatePanel = () => {
    setSelectedArticle(null);
    setPanelMode('create');
    setIsPanelOpen(true);
  };

  const openArticle = async (article: Article, mode: ArticlePanelMode) => {
    setPendingArticleId(article.id);
    try {
      const detail = await getArticleById(article.id);
      setSelectedArticle(detail);
      setPanelMode(mode);
      setIsPanelOpen(true);
    } catch (error: unknown) {
      message.error(toArticlesApiError(error).message);
    } finally {
      setPendingArticleId(null);
    }
  };

  const submitArticle = async (input: CreateArticleInput | UpdateArticleInput) => {
    setIsSubmitting(true);
    try {
      if (panelMode === 'create') {
        await createArticle(input as CreateArticleInput);
        message.success(t('admin.articles.notices.created'));
      } else if (selectedArticle) {
        await updateArticle(selectedArticle.id, input as UpdateArticleInput);
        message.success(t('admin.articles.notices.updated'));
      }
      await refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setPendingArticleId(deleteTarget.id);
    try {
      await deleteArticle(deleteTarget.id);
      message.success(t('admin.articles.notices.deleted'));
      setDeleteTarget(null);
      if (pageData.data.length === 1 && query.page > 1) {
        setPage(query.page - 1);
      } else {
        await refresh();
      }
    } catch (error: unknown) {
      message.error(toArticlesApiError(error).message);
    } finally {
      setPendingArticleId(null);
    }
  };

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.articles.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.articles.subtitle')}</p>
        </div>
        {canCreate && (
          <button type="button" className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary shadow-sm hover:bg-on-primary-fixed-variant sm:w-auto" onClick={openCreatePanel}>
            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">add</span>
            {t('admin.articles.actions.create')}
          </button>
        )}
      </header>

      <section className="flex min-h-[590px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        <div className="border-b border-outline-variant px-4 py-4 sm:px-5">
          <h2 className="font-h2 text-h2 text-on-surface">{t('admin.articles.management_title')}</h2>
          <p className="mt-1 text-body-sm text-secondary">{t('admin.articles.management_description')}</p>
        </div>
        <ArticleToolbar sort={query.sort} pageSize={query.size} onSortChange={setSort} onPageSizeChange={setPageSize} />
        {loadError && pageData.data.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
            <span>{loadError}</span>
            <button type="button" className="shrink-0 font-label-sm text-primary hover:underline" onClick={() => void retry()}>{t('admin.articles.actions.retry')}</button>
          </div>
        )}
        <ArticleTable
          articles={pageData.data}
          currentPage={pageData.currentPage}
          totalPages={pageData.totalPages}
          totalElements={pageData.totalElements}
          pageSize={pageData.pageSize}
          isLoading={isTableLoading}
          isInitialLoading={isInitialLoading}
          error={loadError}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
          pendingArticleId={pendingArticleId}
          onPageChange={setPage}
          onView={(article) => void openArticle(article, 'view')}
          onCreate={openCreatePanel}
          onEdit={(article) => void openArticle(article, 'edit')}
          onDelete={setDeleteTarget}
          onRetry={() => void retry()}
        />
      </section>

      <ArticlePanel open={isPanelOpen} mode={panelMode} article={selectedArticle} isSubmitting={isSubmitting} onClose={() => setIsPanelOpen(false)} onSubmit={submitArticle} />

      {deleteTarget && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && pendingArticleId === null) setDeleteTarget(null);
        }}>
          <section className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl" role="alertdialog" aria-modal="true" aria-labelledby="delete-article-title">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-error-container text-error">delete</span>
              <div className="min-w-0">
                <h2 id="delete-article-title" className="font-h2 text-h2 text-on-surface">{t('admin.articles.delete_dialog.title')}</h2>
                <p className="mt-2 text-body-md text-secondary">{t('admin.articles.delete_dialog.description', { title: deleteTarget.title })}</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" className="min-h-10 rounded-xl border border-outline-variant px-4 font-label-md text-secondary" disabled={pendingArticleId !== null} onClick={() => setDeleteTarget(null)}>{t('admin.articles.actions.cancel')}</button>
              <button type="button" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-error px-4 font-label-md text-on-error disabled:opacity-60" disabled={pendingArticleId !== null} onClick={() => void confirmDelete()}>
                {pendingArticleId !== null && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
                {t(pendingArticleId !== null ? 'admin.articles.actions.deleting' : 'admin.articles.actions.delete')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminArticlesPage;
